// import { useMetricAvailability } from "components/project/useMetricAvailability"
import { useQuery } from "@tanstack/react-query"
import { trpc } from "@tokenterminal/core/utils/trpc"
import { useMemo } from "react"
import { type ProjectDetails } from "utils/trpc"

export enum NAVIGATION_ITEM_TABLE_ENUM {
  CRYPTO_SCREENER = "crypto-screener",
  TOP_TOKENHOLDERS = "top-tokenholders",
  INSIDER_TRANSACTIONS = "insider-transactions",
  TRENDING_CONTRACTS = "trending-contracts",
  TRENDING_WALLETS = "trending-wallets",
  PROJECT_CONTRACTS = "project-contracts",
  COHORT_ANALYSIS = "cohort-analysis",
  STABLECOINS = "stablecoins",
  FUNDRAISES = "fundraises",
  BLOCKCHAIN_COMPARISON = "blockchain-comparison",
}

/**
 * Determines if a project is a blockchain (chain) based on its market sectors
 */
export function isChainProject(projectData: ProjectDetails): boolean {
  const chainMarketSectors = [
    "blockchains-l1",
    "blockchains-l2",
    "blockchains-l3",
  ]
  return projectData.market_sectors.some((sector) =>
    chainMarketSectors.includes(sector.id)
  )
}

export function useProjectNavigation(
  projectSlug: string,
  hasFundraises: boolean,
  projectData?: ProjectDetails
) {
  const { data: projects = [] } = useQuery(
    trpc.projects.getDatasetAvailability.queryOptions()
  )

  const project = projects.find((p) => p.slug === projectSlug)

  const navigations = useMemo(() => {
    const navigations = []

    // Determine if this is a chain project
    const isChain = projectData ? isChainProject(projectData) : false

    // navigations.push({
    //   label: "Crypto screener",
    //   value: NAVIGATION_ITEM_TABLE_ENUM.CRYPTO_SCREENER,
    // })
    if (hasFundraises) {
      navigations.push({
        label: "Fundraises",
        value: NAVIGATION_ITEM_TABLE_ENUM.FUNDRAISES,
      })
    }
    if (project?.marts?.top_tokenholders) {
      navigations.push({
        label: "Top tokenholders",
        value: NAVIGATION_ITEM_TABLE_ENUM.TOP_TOKENHOLDERS,
      })
    }
    if (project?.marts?.insider_transactions) {
      navigations.push({
        label: "Insider transactions",
        value: NAVIGATION_ITEM_TABLE_ENUM.INSIDER_TRANSACTIONS,
      })
    }
    if (project?.marts?.stablecoins) {
      navigations.push({
        label: "Stablecoins",
        value: NAVIGATION_ITEM_TABLE_ENUM.STABLECOINS,
      })
    }

    // For chains: show trending contracts (all smart contracts deployed on the chain)
    // For apps: don't show trending contracts (not relevant)
    if (isChain && project?.marts?.trending_contracts) {
      navigations.push({
        label: "Trending contracts",
        value: NAVIGATION_ITEM_TABLE_ENUM.TRENDING_CONTRACTS,
      })
    }

    if (project?.marts?.trending_wallets) {
      navigations.push({
        label: "Trending wallets",
        value: NAVIGATION_ITEM_TABLE_ENUM.TRENDING_WALLETS,
      })
    }

    // For chains: don't show project contracts (not relevant as chains aren't built as smart contracts)
    // For apps: show project contracts (apps are the result of multiple smart contracts)
    if (!isChain && project?.marts?.project_contracts) {
      navigations.push({
        label: "Project contracts",
        value: NAVIGATION_ITEM_TABLE_ENUM.PROJECT_CONTRACTS,
      })
    }

    if (project?.marts?.cohort_analysis) {
      navigations.push({
        label: "Cohort analysis",
        value: NAVIGATION_ITEM_TABLE_ENUM.COHORT_ANALYSIS,
      })
    }

    return navigations
  }, [hasFundraises, projectData, project])

  return navigations
}
